import { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { authApi } from '@/lib/api'
import { User } from '@/types'

interface RegisterResponse {
  id: number
  name: string
  email: string
  role: string
  status: string
  message: string
}

interface AuthContextType {
  user: User | null
  token: string | null
  login: (email: string, password: string) => Promise<void>
  register: (name: string, email: string, password: string, inviteCode?: string) => Promise<RegisterResponse>
  logout: () => void
  isLoading: boolean
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [token, setToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    const storedToken = localStorage.getItem('token')
    const storedUser = localStorage.getItem('user')

    if (storedToken && storedUser) {
      setToken(storedToken)

      // Always fetch fresh user data before rendering to avoid stale household_id
      authApi.me().then(res => {
        const freshUser = res.data
        localStorage.setItem('user', JSON.stringify(freshUser))
        setUser(freshUser)
      }).catch(() => {
        // Token may be expired — fall back to cached user
        setUser(JSON.parse(storedUser))
      }).finally(() => {
        setIsLoading(false)
      })
    } else {
      setIsLoading(false)
    }
  }, [])

  const login = async (email: string, password: string) => {
    const response = await authApi.login(email, password)
    const { token: newToken, user: newUser } = response.data

    localStorage.setItem('token', newToken)
    localStorage.setItem('user', JSON.stringify(newUser))
    setToken(newToken)
    setUser(newUser)
  }

  const register = async (name: string, email: string, password: string, inviteCode?: string): Promise<RegisterResponse> => {
    const response = await authApi.register(name, email, password, inviteCode)
    const data = response.data

    // Auto-login if approved (first user or invited via household code)
    if (data.status === 'approved') {
      await login(email, password)
    }

    return data
  }

  const logout = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    setToken(null)
    setUser(null)
  }

  return (
    <AuthContext.Provider value={{ user, token, login, register, logout, isLoading }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
